import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import GeneralTable from "../../components/Table/GeneralTable";
import { Icon } from "@iconify/react/dist/iconify.js";
import "./ManageAssignment.scss";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { toast } from "react-toastify";

const ManageClients = () => {
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMember, setSelectedMember] = useState("");
  const [viewType, setViewType] = useState("TEAM");
  const salesManagerUrl = process.env.REACT_APP_SALES_MANAGER;

  const fetchUnassignedClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${salesManagerUrl}/clients/unassigned`,
      );
      setClients(response.data.data || []);
    } catch (error) {
      console.error("Error fetching unassigned clients:", error);
    } finally {
      setLoading(false);
    }
  }, [salesManagerUrl]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      // Fixed: Using correct endpoint
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/sales/list`,
      );

      // Transform the employee data to match what we need
      const employees = (response.data.data || []).map((emp) => ({
        id: emp.empId,
        name: `${emp.firstName} ${emp.lastName}`,
        email: emp.companyEmail,
      }));

      setTeamMembers(employees);
    } catch (error) {
      console.error("Error fetching team members:", error);
      console.log("Team Members Response:", error.response);
    }
  }, []);

  const fetchTeamAssignedClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${salesManagerUrl}/clients/assigned-client/team`,
      );
      setClients(response.data.data || []);
    } catch (error) {
      console.error("Error fetching team clients:", error);
      toast.error("Failed to fetch team clients");
    } finally {
      setLoading(false);
    }
  }, [salesManagerUrl]);

  useEffect(() => {
    if (viewType === "UNASSIGNED") {
      fetchUnassignedClients();
    } else {
      fetchTeamAssignedClients();
    }
    fetchTeamMembers();
  }, [
    viewType,
    fetchUnassignedClients,
    fetchTeamAssignedClients,
    fetchTeamMembers,
  ]);

  const handleAssignClient = async (clientId) => {
    if (!selectedMember) {
      toast.info("Please select a team member");
      return;
    }

    try {
      setAssigning(true);

      await axiosInstance.put(
        `${salesManagerUrl}/clients/assign/${clientId}/employee/${selectedMember}`,
      );

      toast.success("Client assigned successfully");
      setSelectedClient(null);
      setSelectedMember("");

      // Refresh based on current view
      if (viewType === "UNASSIGNED") {
        fetchUnassignedClients();
      } else {
        fetchTeamAssignedClients();
      }
    } catch (error) {
      console.error("Error assigning client:", error);
      toast.error(error?.response?.data?.message || "Failed to assign client");
    } finally {
      setAssigning(false);
    }
  };

  // Prepare data for table with searchable text
  const tableData = clients.map((client) => ({
    ...client,
    searchableText:
      ` ${client.companyName} ${client.email} ${client.phoneNumber} ${client.clientStatus} ${client.status}`.toLowerCase(),
  }));

  // Define columns for the table
  const columns = [
    {
      header: "Client",
      accessor: "companyName",
      render: (value, row) => (
        <div className="client-identity">
          <div className="avatar avatar-square">
            {value?.charAt(0)?.toUpperCase() || "?"}
          </div>

          <div className="client-details">
            <div className="client-company">{value || "—"}</div>

            <div className="client-meta">
              {row.clientCode && <span>Code: {row.clientCode}</span>}
            </div>
          </div>
        </div>
      ),
    },

    {
      header: "Source",
      accessor: "clientSource",
      render: (value) => {
        const sourceClass = {
          CAMPAIGN: "source-campaign",
          REFERRAL: "source-referral",
          WEBSITE: "source-website",
          SOCIAL: "source-social",
        };
        return (
          <span className={`badge ${sourceClass[value] || "badge-default"}`}>
            {value}
          </span>
        );
      },
    },
    {
      header: "Contact Information",
      accessor: "email",
      render: (value, row) => (
        <div className="contact-info">
          <div className="contact-item contact-item-lg">
            <Icon icon="ic:baseline-email" className="contact-icon icon-lg" />
            <span className="contact-text text-lg">{value}</span>
          </div>
          <div className="contact-item contact-item-lg">
            <Icon icon="ic:baseline-phone" className="contact-icon icon-lg" />
            <span className="contact-text text-lg">{row.phoneNumber}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Score",
      accessor: "clientScore",
      render: (value) => (
        <div className="score-display">
          <Icon icon="ic:baseline-star" className="score-icon" />
          <span className="score-value">{value || 0}</span>
        </div>
      ),
    },
    {
      header: "Contact Persons",
      accessor: "contactPersons",
      render: (value) => {
        if (!value || value.length === 0) {
          return <span className="no-contacts">No contacts</span>;
        }
        return (
          <div className="contact-persons-list">
            {value.slice(0, 2).map((contact, idx) => (
              <div key={contact.id || idx} className="contact-person-item">
                <Icon icon="ic:baseline-person" className="person-icon" />
                <span className="person-name">{contact.contactPersonName}</span>
              </div>
            ))}
            {value.length > 2 && (
              <span className="more-contacts">+{value.length - 2} more</span>
            )}
          </div>
        );
      },
    },
    {
      header: "Actions",
      accessor: "id",
      render: (value, row) => {
        const isAssigned = !!row.assignedToId;

        return (
          <div className="actions-cell">
            {selectedClient === value ? (
              <div className="assignment-section">
                {/* Show current assignee when reassigning */}
                {isAssigned && (
                  <div className="current-assignee">
                    <Icon icon="ic:baseline-person" />
                    <span>
                      Currently assigned to{" "}
                      <strong>{row.assignedToName}</strong>
                    </span>
                  </div>
                )}

                <select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  className="member-select select-lg"
                >
                  <option value="">Select Team Member</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>

                <div className="button-group button-group-lg">
                  <ButtonWithIcon
                    icon={
                      assigning
                        ? "svg-spinners:ring-resize"
                        : "ic:baseline-check"
                    }
                    text={
                      assigning
                        ? "Saving..."
                        : isAssigned
                          ? "Reassign"
                          : "Assign"
                    }
                    onClick={() => handleAssignClient(value)}
                    disabled={assigning || !selectedMember}
                    className="btn-confirm btn-lg"
                  />

                  <ButtonWithIcon
                    icon="ic:baseline-close"
                    text="Cancel"
                    onClick={() => {
                      setSelectedClient(null);
                      setSelectedMember("");
                    }}
                    className="btn-cancel btn-lg"
                  />
                </div>
              </div>
            ) : (
              <>
                {isAssigned && (
                  <div className="assigned-info">
                    <Icon icon="ic:baseline-person" />
                    <span className="assigned-name">{row.assignedToName}</span>
                  </div>
                )}

                <ButtonWithIcon
                  icon="ic:baseline-person-add"
                  text={isAssigned ? "Reassign" : "Assign"}
                  onClick={() => setSelectedClient(value)}
                  className={
                    isAssigned ? "btn-reassign btn-lg" : "btn-assign btn-lg"
                  }
                />
              </>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="manage-assignment-container">
      <div className="assignment-toggle">
        <button
          className={`toggle-btn ${viewType === "TEAM" ? "active" : ""}`}
          onClick={() => setViewType("TEAM")}
        >
          Team Clients
        </button>

        <button
          className={`toggle-btn ${viewType === "UNASSIGNED" ? "active" : ""}`}
          onClick={() => setViewType("UNASSIGNED")}
        >
          Unassigned Clients
        </button>
      </div>

      <GeneralTable
        columns={columns}
        data={tableData}
        title={
          viewType === "UNASSIGNED"
            ? "Manage Unassigned Clients"
            : "Team Assigned Clients"
        }
        initialPageSize={10}
        showPagination={true}
      />
    </div>
  );
};

export default ManageClients;
